import React, { useEffect, useMemo, useState } from 'react';

import Paginacao from '../../components/paginacao/Paginacao';
import TabelaSistema, { ColunaTabela } from '../../components/sync-painel/TabelaSistema';
import '../../views/recepcao/pages/Recepcao.css';

/**
 * Tabela paginada dos dashboards (at-86).
 *
 * ⚠️ O desenho da tabela NÃO é próprio: vem do `TabelaSistema`, que é o padrão único das telas do
 * sistema (Recepção, Chefe de Oficina, Usuários, Tela de Controle, Sincronização) — `TabelaScroll`
 * > `TabelaContent` > `Tabela`, com as colunas medidas pelo hook da at-43. Uma tabela com estilo
 * próprio no dashboard seria uma segunda identidade visual para a mesma coisa.
 *
 * O que este componente acrescenta é só a paginação em volta.
 *
 * ⚠️ A paginação é NO CLIENTE, e é decisão: as agregações voltam inteiras do backend (dezenas a
 * centenas de linhas, payload pequeno) e o que pagina é a exibição. Paginar no servidor exigiria
 * repetir filtro, ordenação e recorte em cada rota para ganhar nada — os dados já estão na tela.
 *
 * ⚠️ A página volta para 1 quando os DADOS mudam (troca de filtro, de recorte, de foco). Sem isso,
 * quem está na página 3 vê uma tabela vazia depois de filtrar — e lê como "não há dado", que é o
 * falso estado feliz que a at-60 existe para evitar.
 */

export type { ColunaTabela };

interface Props<T> {
  itens: T[];
  colunas: Array<ColunaTabela<T>>;
  chave: (item: T, indice: number) => string;
  /** Frase da linha única quando não há item. Quem chama decide — a tela sabe se a consulta veio. */
  vazio: string;
  porPagina?: number;
  onClickLinha?: (item: T) => void;
}

function TabelaPaginada<T>({
  itens,
  colunas,
  chave,
  vazio,
  porPagina = 10,
  onClickLinha,
}: Props<T>) {
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    setPagina(1);
  }, [itens]);

  const totalPaginas = Math.max(1, Math.ceil(itens.length / porPagina));
  const visiveis = useMemo(
    () => itens.slice((pagina - 1) * porPagina, pagina * porPagina),
    [itens, pagina, porPagina],
  );

  return (
    <>
      <TabelaSistema
        colunas={colunas}
        itens={visiveis}
        chave={(item) => chave(item, itens.indexOf(item))}
        vazio={vazio}
        onClickLinha={onClickLinha}
      />

      {totalPaginas > 1 && (
        <div className="do-paginacao">
          <Paginacao paginaAtual={pagina} totalPaginas={totalPaginas} onMudarPagina={setPagina} />
        </div>
      )}
    </>
  );
}

export default TabelaPaginada;
